import React, { useState } from 'react';
import { MapPin, ExternalLink, Navigation, Car, Train, Maximize2, Minimize2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

interface EmbeddedMapProps {
  /**
   * Provider location (e.g., "Brussel, België")
   */
  location: string;
  /**
   * Full address if available
   */
  address?: string;
  /**
   * Service type to determine if map should be shown
   */
  serviceType?: 'online' | 'in-person' | 'both';
  /**
   * Provider name for context
   */
  providerName?: string;
}

const EmbeddedMap: React.FC<EmbeddedMapProps> = ({ 
  location, 
  address, 
  serviceType = 'in-person',
  providerName = 'Provider'
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Don't show map for online-only services
  if (serviceType === 'online') {
    return null;
  }

  const destination = address || location;
  const encodedDestination = encodeURIComponent(destination);

  // Create Google Maps embed URL
  const embedUrl = `https://www.google.com/maps/embed/v1/place?key=YOUR_API_KEY&q=${encodedDestination}&zoom=15`;
  
  // For demo purposes, we'll use a static map URL since we don't have API key
  const staticMapUrl = `https://maps.googleapis.com/maps/api/staticmap?center=${encodedDestination}&zoom=15&size=600x300&maptype=roadmap&markers=color:red%7C${encodedDestination}&key=YOUR_API_KEY`;

  // Belgium-specific navigation functions
  const openGoogleMaps = () => {
    const url = `https://www.google.com/maps/search/?api=1&query=${encodedDestination}`;
    window.open(url, '_blank');
    toast.success('Google Maps geopend');
  };

  const openWaze = () => {
    const url = `https://waze.com/ul?q=${encodedDestination}`;
    window.open(url, '_blank');  
    toast.success('Waze geopend');
  };

  const openNMBS = () => {
    const url = `https://www.belgiantrain.be/nl/travel-info/prepare-for-your-journey/route-planner`;
    window.open(url, '_blank');
    toast.success('NMBS Routeplanner geopend');
  };

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
    toast.info(isExpanded ? 'Kaart verkleind' : 'Kaart vergroot');
  };

  return (
    <Card className="shadow-sm" style={{ 
      borderColor: '#efe7dd', 
      backgroundColor: '#fbf8f4' 
    }}>
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg text-gray-900 flex items-center gap-2">
            <MapPin className="h-5 w-5" style={{ color: '#b6a092' }} />
            Locatie & Route
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleExpanded}
            className="hover:bg-beige-100"
          >
            {isExpanded ? (
              <Minimize2 className="h-4 w-4" style={{ color: '#b6a092' }} />
            ) : (
              <Maximize2 className="h-4 w-4" style={{ color: '#b6a092' }} />
            )}
          </Button>
        </div>
        <div className="flex items-center gap-2 text-sm" style={{ color: '#a0897c' }}>
          <MapPin className="h-4 w-4" style={{ color: '#b6a092' }} />
          <span>{destination}</span>
        </div>
        {serviceType === 'both' && (
          <Badge variant="secondary" className="w-fit" style={{ 
            backgroundColor: '#efe7dd', 
            color: '#a0897c' 
          }}>
            Online & Op locatie mogelijk
          </Badge>
        )}
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Embedded Map Preview */}
        <div className={`relative rounded-lg overflow-hidden ${isExpanded ? 'h-96' : 'h-48'} transition-all duration-300`}>
          {/* Demo: Static map placeholder with Belgian styling */}
          <div 
            className="w-full h-full bg-gradient-to-br from-green-100 to-blue-100 flex items-center justify-center relative"
            style={{ 
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23b6a092' fill-opacity='0.1'%3E%3Circle cx='30' cy='30' r='4'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")` 
            }}
          >
            {/* Map placeholder with Belgian flag colors */}
            <div className="absolute inset-0 bg-gradient-to-br from-black/10 via-yellow-500/10 to-red-500/10" />
            
            {/* Location marker */}
            <div className="relative z-10 text-center">
              <div className="w-12 h-12 rounded-full mx-auto mb-2 flex items-center justify-center shadow-lg" style={{ backgroundColor: '#b6a092' }}>
                <MapPin className="h-6 w-6 text-white" />
              </div>
              <div className="bg-white/90 backdrop-blur-sm rounded-lg p-3 shadow-sm">
                <p className="text-sm font-medium text-gray-900">{providerName}</p>
                <p className="text-xs" style={{ color: '#a0897c' }}>{location}</p>
              </div>
            </div>

            {/* Mock map grid overlay */}
            <div className="absolute inset-0 opacity-20">
              <div className="grid grid-cols-6 grid-rows-4 h-full w-full">
                {Array.from({ length: 24 }).map((_, i) => (
                  <div key={i} className="border border-gray-300/50" />
                ))}
              </div>
            </div>
          </div>

          {/* Map overlay info */}
          <div className="absolute bottom-3 left-3 right-3">
            <div className="bg-white/90 backdrop-blur-sm rounded-lg p-3 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-900">📍 {location}</p>
                  <p className="text-xs" style={{ color: '#a0897c' }}>
                    Klik om te navigeren
                  </p>
                </div>
                <Button
                  size="sm"
                  onClick={openGoogleMaps}
                  className="text-white text-xs"
                  style={{ backgroundColor: '#b6a092' }}
                >
                  <Navigation className="h-3 w-3 mr-1" />
                  Route
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Options */}
        <div className="grid grid-cols-2 gap-3">
          {/* Google Maps */}
          <Button
            onClick={openGoogleMaps}
            variant="outline"
            className="flex items-center gap-2 py-3 hover:bg-beige-100"
            style={{ 
              borderColor: '#e6dbd0',
              color: '#837069'
            }}
          >
            <Car className="h-4 w-4" style={{ color: '#b6a092' }} />
            <div className="text-xs">
              <div className="font-medium">Google Maps</div>
            </div>
            <ExternalLink className="h-3 w-3 ml-auto" style={{ color: '#b6a092' }} />
          </Button>

          {/* Waze */}
          <Button
            onClick={openWaze}
            variant="outline"
            className="flex items-center gap-2 py-3 hover:bg-beige-100"
            style={{ 
              borderColor: '#e6dbd0',
              color: '#837069'
            }}
          >
            <Navigation className="h-4 w-4" style={{ color: '#b6a092' }} />
            <div className="text-xs">
              <div className="font-medium">Waze</div>
            </div>
            <ExternalLink className="h-3 w-3 ml-auto" style={{ color: '#b6a092' }} />
          </Button>
        </div>

        {/* Public Transport Option */}
        <Button
          onClick={openNMBS}
          variant="outline"
          className="w-full flex items-center gap-2 py-3 hover:bg-beige-100"
          style={{ 
            borderColor: '#e6dbd0',
            color: '#837069'
          }}
        >
          <Train className="h-4 w-4" style={{ color: '#b6a092' }} />
          <div className="text-xs text-left flex-1">
            <div className="font-medium">NMBS Openbaar Vervoer</div>
            <div style={{ color: '#b6a092' }}>Treinen & bussen in België</div>
          </div>
          <ExternalLink className="h-3 w-3" style={{ color: '#b6a092' }} />
        </Button>

        {/* Distance & Travel Info */}
        {isExpanded && (
          <div className="space-y-4">
            {/* Travel Times */}
            <div className="rounded-lg p-3 space-y-2" style={{ 
              backgroundColor: '#efe7dd', 
              borderColor: '#e6dbd0',
              border: '1px solid'
            }}>
              <h4 className="font-medium text-sm" style={{ color: '#837069' }}>
                Geschatte reistijd vanuit centrum
              </h4>
              <div className="grid grid-cols-3 gap-3 text-xs">
                <div className="text-center">
                  <Car className="h-4 w-4 mx-auto mb-1" style={{ color: '#b6a092' }} />
                  <div className="font-medium" style={{ color: '#837069' }}>15-25 min</div>
                  <div style={{ color: '#b6a092' }}>Auto</div>
                </div>
                <div className="text-center">
                  <Train className="h-4 w-4 mx-auto mb-1" style={{ color: '#b6a092' }} />
                  <div className="font-medium" style={{ color: '#837069' }}>20-35 min</div>
                  <div style={{ color: '#b6a092' }}>Openbaar vervoer</div>
                </div>
                <div className="text-center">
                  <MapPin className="h-4 w-4 mx-auto mb-1" style={{ color: '#b6a092' }} />
                  <div className="font-medium" style={{ color: '#837069' }}>~8 km</div>
                  <div style={{ color: '#b6a092' }}>Afstand</div>
                </div>
              </div>
            </div>

            {/* Belgium-specific location info */}
            <div className="rounded-lg p-3" style={{ 
              backgroundColor: '#fbf8f4', 
              borderColor: '#efe7dd',
              border: '1px solid'
            }}>
              <h4 className="font-medium text-sm mb-2" style={{ color: '#837069' }}>
                🇧🇪 Lokale info
              </h4>
              <div className="space-y-2 text-xs">
                <div className="flex items-center justify-between">
                  <span style={{ color: '#b6a092' }}>Parkeren:</span>
                  <span style={{ color: '#837069' }}>Betaald parkeren beschikbaar</span>
                </div>
                <div className="flex items-center justify-between">
                  <span style={{ color: '#b6a092' }}>Openbaar vervoer:</span>
                  <span style={{ color: '#837069' }}>Bus & tram haltes nabij</span>
                </div>
                <div className="flex items-center justify-between">
                  <span style={{ color: '#b6a092' }}>Toegankelijkheid:</span>
                  <span style={{ color: '#837069' }}>Rolstoeltoegankelijk</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Info Text */}
        <div className="text-center text-xs" style={{ color: '#a0897c' }}>
          💡 Tip: Download je favoriete navigatie app voor de beste routebeschrijving
        </div>
      </CardContent>
    </Card>
  );
};

export default EmbeddedMap;